/**
 * Today + Pipeline module contracts (Pranav v10.346 "Today" and "Pipeline" tabs).
 * Every number here is computed by the server in code. AI outputs are nullable: null means
 * "AI couldn't help this time — you can do it by hand". Nothing here is sent to a client/contact.
 */
import { z } from 'zod';
import { Id, Inr, IsoDate, Timestamp, VerticalKey } from './common.js';
import { Ball, Relationship, Section } from './relationships.js';
import { Channel, LogEntry } from './activity.js';

export const TP_PREFIX = '/tp';

// ---------------------------------------------------------------- shared row facts
export const RowFlag = z.enum(['stale', 'no_next', 'has_date', 'your_turn', 'two_messages', 'readiness_gap', 'sponsor', 'deadline_known', 'unanswered']);
export type RowFlag = z.infer<typeof RowFlag>;

export const RowBrief = z.object({
  relationshipId: Id,
  contactName: z.string(),
  company: z.string(),
  verticalKey: VerticalKey,
  section: Section,
  ball: Ball,
  watch: IsoDate.nullable(),
  lastContact: IsoDate.nullable(),
  /** Whole IST days since lastContact, computed in code. */
  daysSinceContact: z.number().int().nullable(),
  next: z.string().nullable(),
  status: z.string().nullable(),
  touches: z.number().int().nonnegative(),
  flags: z.array(RowFlag),
  /** Optimistic-concurrency version of the relationship (updated_at ms). */
  version: z.number().int().nonnegative(),
});
export type RowBrief = z.infer<typeof RowBrief>;

// ---------------------------------------------------------------- Today board
export const TodayBoardQuery = z.object({ vertical: VerticalKey.optional(), product: z.string().max(80).optional() });

export const InboundAck = z.object({
  relationshipId: Id,
  contactName: z.string(),
  company: z.string(),
  receivedOn: IsoDate,
  message: z.string(),
  law: z.literal('L117'),
});
export const PromiseDue = z.object({
  commitmentId: Id,
  relationshipId: Id,
  contactName: z.string(),
  company: z.string(),
  text: z.string(),
  by: z.enum(['us', 'them']),
  dueDate: IsoDate.nullable(),
  overdue: z.boolean(),
  version: z.number().int().nonnegative(),
});
export const FestivalItem = z.object({ name: z.string(), date: IsoDate, daysAway: z.number().int(), fiscal: z.boolean() });
export const PlanItem = z.object({ id: z.string(), title: z.string(), action: z.string(), dueWeek: IsoDate.nullable(), owner: z.string(), overdue: z.boolean() });
export const ActivityDay = z.object({ date: IsoDate, label: z.string(), sent: z.number().int().nonnegative(), received: z.number().int().nonnegative() });
export const ProductOption = z.object({ id: Id, key: z.string(), name: z.string() });

export const TodayKpis = z.object({
  dueToday: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  repliesWaiting: z.number().int().nonnegative(),
  promisesDue: z.number().int().nonnegative(),
  yourTurn: z.number().int().nonnegative(),
  stale: z.number().int().nonnegative(),
  paying: z.number().int().nonnegative(),
  hot: z.number().int().nonnegative(),
  pipelineValueInr: Inr,
  /** e.g. "₹12.5 lakh", formatted in code. */
  pipelineValueLabel: z.string(),
});

export const StoredRun = z.object({ ranAt: Timestamp, time: z.string(), output: z.unknown() });

export const TodayBoard = z.object({
  date: IsoDate,
  timezone: z.literal('Asia/Kolkata'),
  isMonday: z.boolean(),
  kpis: TodayKpis,
  dataHealth: z.object({ clean: z.boolean(), rows: z.array(RowBrief) }),
  /** Monday ritual: the learning loop has not run for 7+ days. */
  learningLoopDue: z.boolean(),
  inboundAcks: z.array(InboundAck),
  overdue: z.array(RowBrief),
  dueToday: z.array(RowBrief),
  comingUp: z.array(RowBrief),
  yourTurn: z.array(RowBrief),
  resting: z.array(RowBrief),
  stale: z.array(RowBrief),
  noNext: z.array(RowBrief),
  promises: z.array(PromiseDue),
  festivals: z.array(FestivalItem),
  plan: z.array(PlanItem),
  activity: z.array(ActivityDay),
  products: z.array(ProductOption),
  hiddenByProduct: z.array(RowBrief),
  lastTriage: StoredRun.nullable(),
  lastChiefMoves: StoredRun.nullable(),
  activeRows: z.number().int().nonnegative(),
});
export type TodayBoard = z.infer<typeof TodayBoard>;

// ---------------------------------------------------------------- AI results (null = do it by hand)
export const TriageOutput = z.object({
  courtesy: z.array(z.object({ id: Id, message: z.string() })),
  due: z.array(z.object({ id: Id, why: z.string(), suggested_action: z.string() })),
  watch_fixes: z.array(z.object({ id: Id, current_watch: z.string().nullable(), proposal: z.string(), why: z.string() })),
  journey: z.array(z.object({ id: Id, from_stage: z.string(), to_stage: z.string(), what_moves_it: z.string(), first_step: z.string() })),
  waiting_note: z.string(),
});
export type TriageOutput = z.infer<typeof TriageOutput>;

export const ChiefMovesOutput = z.object({
  chief_moves: z.array(z.object({ title: z.string(), insight: z.string(), applies_to: z.array(Id), excluded_note: z.string(), first_step: z.string() })),
});
export type ChiefMovesOutput = z.infer<typeof ChiefMovesOutput>;

export const AskOutput = z.object({ answer: z.string(), row_ids: z.array(Id) });
export type AskOutput = z.infer<typeof AskOutput>;

export const AiResult = <T extends z.ZodTypeAny>(o: T) =>
  z.object({ output: o.nullable(), ranAt: Timestamp, names: z.record(Id, z.string()).default({}) });

export const TriageResult = AiResult(TriageOutput);
export type TriageResult = z.infer<typeof TriageResult>;
export const ChiefMovesResult = AiResult(ChiefMovesOutput);
export type ChiefMovesResult = z.infer<typeof ChiefMovesResult>;
export const AskRequest = z.object({ question: z.string().trim().min(3).max(500), vertical: VerticalKey.optional() }).strict();
export const AskResult = AiResult(AskOutput);
export type AskResult = z.infer<typeof AskResult>;
export const RunTodayRequest = z.object({ vertical: VerticalKey.optional() }).strict();

export const FestivalDismissRequest = z.object({ name: z.string().min(1).max(120), date: IsoDate }).strict();

// ---------------------------------------------------------------- row quick actions (server records a Note in history)
export const WatchActionRequest = z.discriminatedUnion('action', [
  z.object({ action: z.literal('snooze'), days: z.union([z.literal(7), z.literal(14)]), version: z.number().int().nonnegative() }).strict(),
  z.object({ action: z.literal('clear'), version: z.number().int().nonnegative() }).strict(),
  z.object({ action: z.literal('set'), date: IsoDate, reason: z.string().max(300).optional(), version: z.number().int().nonnegative() }).strict(),
]);
export type WatchActionRequest = z.infer<typeof WatchActionRequest>;
export const VersionOnly = z.object({ version: z.number().int().nonnegative() }).strict();
export const CloseJourneyRequest = z.object({ version: z.number().int().nonnegative(), outcome: z.string().max(80).optional(), reason: z.string().max(500).optional() }).strict();
/** Record that the owner sent a message outside the app (e.g. a courtesy note from triage). */
export const QuickSentRequest = z.object({ channel: Channel.exclude(['Note']), summary: z.string().trim().min(1).max(2000) }).strict();

// ---------------------------------------------------------------- relationship detail (all prototype row fields)
export const CLOSE_OUTCOMES = ['Won, became customer', 'Lost to status quo', 'Lost to competitor or agency', 'No budget or wrong pool', 'Timing, revisit later', 'Died in vendor process', 'Relationship preserved, dormant', 'Went silent'] as const;
export const Readiness = z.object({
  sponsor: z.boolean(), liveEvent: z.boolean(), deadline: z.boolean(),
  firstUser: z.boolean(), clearance: z.boolean(), budgetOk: z.boolean(),
  successAgreed: z.boolean(), vendorRegistered: z.boolean(), multiThread: z.boolean(),
});
export type Readiness = z.infer<typeof Readiness>;

export const RelationshipDetail = z.object({
  relationship: Relationship,
  contactName: z.string(),
  company: z.string(),
  division: z.string(),
  designation: z.string().nullable(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  ownerName: z.string(),
  type: z.string().nullable(),
  origin: z.string().nullable(),
  channelPref: z.string().nullable(),
  langPref: z.string().nullable(),
  roles: z.string().nullable(),
  linkedinWarmth: z.string().nullable(),
  whatsappEarned: z.boolean(),
  deadlineDate: IsoDate.nullable(),
  payingSince: IsoDate.nullable(),
  /** Whole months, computed in code. */
  tenureMonths: z.number().int().nullable(),
  closeOutcome: z.string().nullable(),
  closeReason: z.string().nullable(),
  profileLines: z.array(z.string()),
  concerns: z.array(z.string()),
  resonates: z.array(z.string()),
  sponsorName: z.string().nullable(),
  eventType: z.string().nullable(),
  firstUserName: z.string().nullable(),
  planInterest: z.string().nullable(),
  productIds: z.array(Id),
  readiness: Readiness,
  statusBy: z.string().nullable(),
  statusOn: IsoDate.nullable(),
  lastContact: IsoDate.nullable(),
  latest: z.array(LogEntry),
  /** Suggested next contact date from code (L-doctrine), with plain reason. */
  suggestedWatch: z.object({ date: IsoDate, why: z.string() }).nullable(),
  reads: z.record(z.string(), z.object({ at: Timestamp, output: z.unknown() })),
});
export type RelationshipDetail = z.infer<typeof RelationshipDetail>;

export const UpdateDetailRequest = z.object({
  version: z.number().int().nonnegative(),
  type: z.string().max(40).nullable().optional(),
  origin: z.string().max(200).nullable().optional(),
  channelPref: z.string().max(120).nullable().optional(),
  roles: z.string().max(500).nullable().optional(),
  linkedinWarmth: z.enum(['watching', 'warming', 'engaged', 'hot']).nullable().optional(),
  whatsappEarned: z.boolean().optional(),
  deadlineDate: IsoDate.nullable().optional(),
  payingSince: IsoDate.nullable().optional(),
  closeOutcome: z.string().max(80).nullable().optional(),
  closeReason: z.string().max(500).nullable().optional(),
  profileLines: z.array(z.string().trim().min(1).max(500)).max(200).optional(),
  concerns: z.array(z.string().trim().min(1).max(300)).max(50).optional(),
  resonates: z.array(z.string().trim().min(1).max(300)).max(50).optional(),
  sponsorName: z.string().max(120).nullable().optional(),
  eventType: z.string().max(120).nullable().optional(),
  firstUserName: z.string().max(120).nullable().optional(),
  planInterest: z.string().max(120).nullable().optional(),
  productIds: z.array(Id).max(50).optional(),
  readiness: Readiness.partial().optional(),
}).strict();
export type UpdateDetailRequest = z.infer<typeof UpdateDetailRequest>;

export const UpdateLogRequest = z.object({
  date: IsoDate.optional(),
  dir: z.enum(['in', 'out']).optional(),
  channel: Channel.optional(),
  summary: z.string().trim().min(1).max(5000).optional(),
}).strict();

// ---------------------------------------------------------------- per-row AI reads
export const SynthOutput = z.object({
  status: z.string(),
  next: z.string(),
  action: z.enum(['touch_now', 'hold_and_wait', 'diagnose_blocker', 'reactivate_with_reason', 'fill_activation_gap', 'move_to_closed']),
  reason: z.string(),
  memory_note: z.string(),
  view_response: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
});
export type SynthOutput = z.infer<typeof SynthOutput>;
export const StageOutput = z.object({ stage: Section, reason: z.string(), confidence: z.enum(['high', 'medium', 'low']) });
export type StageOutput = z.infer<typeof StageOutput>;
export const IdeasOutput = z.object({
  ideas: z.array(z.object({ title: z.string(), action: z.string(), source: z.string(), why_valuable: z.string(), idea_type: z.enum(['external', 'authored', 'network']) })),
});
export type IdeasOutput = z.infer<typeof IdeasOutput>;
export const FixOutput = z.object({
  new_status: z.string(),
  new_next: z.string(),
  log_entries_to_remove: z.array(z.object({ date: z.string(), summary_contains: z.string() })),
  log_entry_to_add: z.object({ date: z.string(), summary: z.string() }).nullable(),
  explanation: z.string(),
});
export type FixOutput = z.infer<typeof FixOutput>;
export const PrepOutput = z.object({
  who: z.string(),
  opening: z.string(),
  gates: z.array(z.object({ gate: z.string(), question: z.string(), known: z.string().nullable() })),
  objections: z.array(z.object({ objection: z.string(), answer: z.string() })),
  listen_for: z.array(z.string()),
  next_step: z.string(),
});
export type PrepOutput = z.infer<typeof PrepOutput>;

export const SynthRequest = z.object({ note: z.string().trim().max(2000).optional() }).strict();
export const IdeasRequest = z.object({ missingAsset: z.string().trim().max(500).optional() }).strict();
export const FixRequest = z.object({ concern: z.string().trim().min(5).max(2000) }).strict();
export const EmptyRequest = z.object({}).strict();

export const SynthResult = AiResult(SynthOutput);
export const StageResult = AiResult(StageOutput);
export const IdeasResult = AiResult(IdeasOutput);
/** Fix proposal: the server resolves which log entries the AI means (by id) so the owner sees them before applying. */
export const FixResult = z.object({
  output: FixOutput.nullable(),
  ranAt: Timestamp,
  names: z.record(Id, z.string()).default({}),
  removeLogIds: z.array(Id),
});
export const PrepResult = AiResult(PrepOutput);
export type FixResult = z.infer<typeof FixResult>;

export const ApplyFixRequest = z.object({
  version: z.number().int().nonnegative(),
  status: z.string().trim().max(2000),
  next: z.string().trim().max(2000),
  removeLogIds: z.array(Id).max(20),
  addNote: z.string().trim().max(2000).optional(),
}).strict();

// ---------------------------------------------------------------- pipeline
export const PipelineQuery = z.object({ vertical: VerticalKey.optional(), archived: z.enum(['true', 'false']).optional() });
export const PipelineRow = RowBrief.extend({
  verticalId: Id,
  ownerUserId: Id,
  ownerName: z.string(),
  dealValue: Inr.nullable(),
  blocker: z.string().nullable(),
  type: z.string().nullable(),
  archived: z.boolean(),
});
export type PipelineRow = z.infer<typeof PipelineRow>;
export const StageSummary = z.object({ section: Section, count: z.number().int().nonnegative(), valueInr: Inr });
export const PipelineResponse = z.object({
  items: z.array(PipelineRow),
  stages: z.array(StageSummary),
  owners: z.array(z.object({ id: Id, name: z.string() })),
  /** Sum of deal values of rows not in Closed, computed by the server. */
  openValueInr: Inr,
  truncated: z.boolean(),
});
export type PipelineResponse = z.infer<typeof PipelineResponse>;

/** One person can belong to several business areas. Send `verticalIds` (preferred); `verticalId` is kept for old callers. */
export const AreaIds = z.array(Id).min(1, 'choose at least one business area').max(20);
export const AddPersonRequest = z.object({
  verticalId: Id.optional(),
  verticalIds: AreaIds.optional(),
  name: z.string().trim().min(1).max(200),
  company: z.string().trim().min(1).max(200),
  division: z.string().trim().max(200).optional(),
  designation: z.string().trim().max(200).optional(),
  email: z.string().trim().email().max(254).optional(),
  phone: z.string().regex(/^\+[1-9]\d{6,14}$/, 'phone must be like +919812345678').optional(),
  section: Section.default('O'),
  ownerUserId: Id.optional(),
  dealValue: Inr.optional(),
  watch: IsoDate.optional(),
  next: z.string().trim().max(2000).optional(),
  origin: z.string().trim().max(200).optional(),
  confirmNotDuplicate: z.boolean().optional(),
  /** Add an ALREADY KNOWN person to more business areas: no new contact is made; areas they are in are skipped. */
  contactId: Id.optional(),
}).strict();
export type AddPersonRequest = z.infer<typeof AddPersonRequest>;
export const DuplicateHit = z.object({ contactId: Id, name: z.string(), company: z.string().nullable(), matchedOn: z.array(z.enum(['name', 'email', 'phone'])) });
/** An area that was not added, with a plain reason ("Already in Pharma"). */
export const SkippedArea = z.object({ verticalId: Id, reason: z.string() });
export type SkippedArea = z.infer<typeof SkippedArea>;
export const AddPersonResponse = z.object({
  /** The first entry created (null when a possible duplicate stopped the save, or every area was skipped). */
  relationship: Relationship.nullable(),
  /** Every pipeline entry created, one per business area. */
  relationships: z.array(Relationship).default([]),
  skippedAreas: z.array(SkippedArea).default([]),
  duplicates: z.array(DuplicateHit),
});
export type AddPersonResponse = z.infer<typeof AddPersonResponse>;

export const BulkReassignRequest = z.object({ ids: z.array(Id).min(1).max(200), toUserId: Id, reason: z.string().trim().max(500).optional() }).strict();
export const BulkReassignResponse = z.object({
  moved: z.number().int().nonnegative(),
  skipped: z.array(z.object({ id: Id, reason: z.string() })),
});
export type BulkReassignResponse = z.infer<typeof BulkReassignResponse>;

export const ImportRow = z.object({
  name: z.string().trim().min(1).max(200),
  company: z.string().trim().min(1).max(200),
  designation: z.string().trim().max(200).optional(),
  email: z.string().trim().max(254).optional(),
  phone: z.string().trim().max(20).optional(),
  /** Several business areas for this row (from an "areas" column). Empty or absent = the request's areas. */
  verticalIds: z.array(Id).max(20).optional(),
});
export const ImportRequest = z.object({ verticalId: Id.optional(), verticalIds: AreaIds.optional(), section: Section.default('O'), rows: z.array(ImportRow).min(1).max(500) }).strict();
export const ImportResponse = z.object({
  /** People (contacts) created. */
  created: z.number().int().nonnegative(),
  /** Pipeline entries created (one per person per business area). */
  entries: z.number().int().nonnegative().default(0),
  skipped: z.array(z.object({ line: z.number().int().positive(), name: z.string(), reason: z.string() })),
});
export type ImportResponse = z.infer<typeof ImportResponse>;

export const DuplicateGroups = z.object({
  groups: z.array(z.object({ key: z.string(), reason: z.string(), rows: z.array(z.object({ relationshipId: Id, contactName: z.string(), company: z.string(), section: Section })) })),
});
export type DuplicateGroups = z.infer<typeof DuplicateGroups>;

/** Route table for this module (prefix /api/v1). */
export const tpRoutes = {
  todayBoard: { method: 'GET', path: '/tp/today/board', query: TodayBoardQuery, response: TodayBoard },
  runTriage: { method: 'POST', path: '/tp/today/triage', body: RunTodayRequest, response: TriageResult },
  runChiefMoves: { method: 'POST', path: '/tp/today/chief-moves', body: RunTodayRequest, response: ChiefMovesResult },
  askRegister: { method: 'POST', path: '/tp/today/ask', body: AskRequest, response: AskResult },
  dismissFestival: { method: 'POST', path: '/tp/today/festivals/dismiss', body: FestivalDismissRequest, response: z.object({ ok: z.literal(true) }) },
  watchAction: { method: 'POST', path: '/tp/relationships/:id/watch', body: WatchActionRequest, response: Relationship },
  skipToday: { method: 'POST', path: '/tp/relationships/:id/skip-today', body: EmptyRequest, response: z.object({ ok: z.literal(true) }) },
  theirTurn: { method: 'POST', path: '/tp/relationships/:id/their-turn', body: VersionOnly, response: Relationship },
  closeJourney: { method: 'POST', path: '/tp/relationships/:id/close-journey', body: CloseJourneyRequest, response: Relationship },
  quickSent: { method: 'POST', path: '/tp/relationships/:id/sent', body: QuickSentRequest, response: LogEntry },
  getDetail: { method: 'GET', path: '/tp/relationships/:id/detail', response: RelationshipDetail },
  updateDetail: { method: 'PATCH', path: '/tp/relationships/:id/detail', body: UpdateDetailRequest, response: RelationshipDetail },
  updateLog: { method: 'PATCH', path: '/tp/log/:id', body: UpdateLogRequest, response: LogEntry },
  deleteLog: { method: 'DELETE', path: '/tp/log/:id', response: z.undefined() },
  synthesise: { method: 'POST', path: '/tp/relationships/:id/ai/synthesise', body: SynthRequest, response: SynthResult },
  stageCheck: { method: 'POST', path: '/tp/relationships/:id/ai/stage-check', body: EmptyRequest, response: StageResult },
  freshIdeas: { method: 'POST', path: '/tp/relationships/:id/ai/fresh-ideas', body: IdeasRequest, response: IdeasResult },
  officeFix: { method: 'POST', path: '/tp/relationships/:id/ai/fix', body: FixRequest, response: FixResult },
  applyFix: { method: 'POST', path: '/tp/relationships/:id/ai/fix/apply', body: ApplyFixRequest, response: Relationship },
  meetingPrep: { method: 'POST', path: '/tp/relationships/:id/ai/meeting-prep', body: EmptyRequest, response: PrepResult },
  pipeline: { method: 'GET', path: '/tp/pipeline', query: PipelineQuery, response: PipelineResponse },
  addPerson: { method: 'POST', path: '/tp/pipeline/people', body: AddPersonRequest, response: AddPersonResponse },
  bulkReassign: { method: 'POST', path: '/tp/pipeline/bulk-reassign', body: BulkReassignRequest, response: BulkReassignResponse },
  importPeople: { method: 'POST', path: '/tp/pipeline/import', body: ImportRequest, response: ImportResponse },
  duplicates: { method: 'GET', path: '/tp/pipeline/duplicates', query: PipelineQuery, response: DuplicateGroups },
} as const;
export type TpRouteName = keyof typeof tpRoutes;
